import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';
import {ProfileService} from '../profile.service';
import {CartesianMeasureValue, QualityIndicator, RainNode, TeamNode} from 'raain-model';
import {DateRange, MapLatLng, TimeframeContainers} from 'raain-ui';
import {CompareManager, FrameSet, mapDateRangeToString, RefreshManager} from '../tools';
import {XYType} from '../xytype';
import {RadarService} from '../radar.service';
import {Storage} from '../storage.service';

let TEST_DETECTION = 0;

@Component({
    selector: 'raain-details',
    templateUrl: 'raain-details.component.html',
    styleUrls: ['raain-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RaainDetailsComponent implements OnDestroy, OnChanges {
    private static readonly HOUR_MS = 60 * 60000;
    private static readonly DAY_MS = 24 * 60 * 60 * 1000;

    @Input()
    public toggleAdmin: boolean;
    @Input()
    public rainNode: RainNode;
    @Input()
    public compareManager: CompareManager;
    @Input()
    public refreshManager: RefreshManager;
    @Input()
    public profileService: ProfileService;
    @Input()
    public radarService: RadarService;

    public availableProviders: string[] = [];
    public availableTimeSteps: number[] = [];
    public selectedProvider: string;
    public selectedTimeStep: number;

    public periodBegin: Date;
    public periodEnd: Date;

    // public UI variables
    public teamNode: TeamNode;
    public periodBeginAsString: string;
    public periodEndAsString: string;
    public periodDurationAsString: string;
    public dateShown: Date;
    public coordinates: MapLatLng;
    public borders: MapLatLng[];
    public timeframeContainers: TimeframeContainers;
    public currentTimeframeTarget: FrameSet;
    public timeframeDates: Date[];
    public countPoints: {
        label: string;
        style: string;
        values: {date: Date; value: number}[];
    }[];
    public countsPeriod: {
        percentImages?: XYType[];
        queueRunning?: number;
        progress?: number;
    };

    public gaugeSelectedPoints: {
        label: string;
        style: string;
        values: {date: Date; value: number}[];
    }[];
    public toggleHistory: boolean;
    public toggleMap: boolean;
    public toggleCompare: boolean;
    public toggleGaugeMeasures: boolean;
    public toggleRemoveCompareDuplicate: boolean;
    public toggleCumulative: boolean;
    public refreshInProgress: boolean;
    public sumDetails: string;
    public showPixelMarkers: boolean = false;
    public isAdmin: boolean;
    public showFullError: boolean;
    public showQualityModal: boolean;
    public qualityIndicators: QualityIndicator[] = [];
    public qualityIndicatorsLoading: boolean = false;

    // Cached computed values (to avoid method calls in templates)
    public percentOfComputations: number = 0;
    public percentOfImages: number = 0;
    public truncatedError: string = '';
    public cumulativeDurationInMinutes: number = 10;
    protected readonly DateRange = DateRange;

    constructor(
        protected storage: Storage,
        private readonly cdr: ChangeDetectorRef
    ) {}

    private static PeriodDisplay(date: Date | string) {
        let d = new Date();
        if (date) {
            d = new Date(date);
            const userTimezoneOffset = d.getTimezoneOffset() * 60000;
            d = new Date(d.getTime() - userTimezoneOffset);
        }
        const exampleFormattedDate = '2017-06-01T08:30';
        return d.toISOString().substring(0, exampleFormattedDate.length);
    }

    private static DateUTC(date: string) {
        const hasISOFormat = date.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/);
        let d = new Date(date);
        if (!hasISOFormat) {
            const userTimezoneOffset = d.getTimezoneOffset() * 60000;
            d = new Date(d.getTime() - userTimezoneOffset);
        }
        return d;
    }

    private static MapCountToDateValue(c: XYType): {date: Date; value: number} {
        return {
            date: RaainDetailsComponent.DateUTC(c.name),
            value: Math.min(100, c.x),
        };
    }

    async openQualityModal() {
        this.showQualityModal = true;
        this.qualityIndicatorsLoading = true;
        this.qualityIndicators = [];
        this.cdr.markForCheck();

        if (this.rainNode?.id) {
            const result = await this.profileService.getIndicators(this.rainNode.id);
            this.qualityIndicators = result.indicators;
        }

        this.qualityIndicatorsLoading = false;
        this.cdr.markForCheck();
    }

    closeQualityModal() {
        this.showQualityModal = false;
    }

    formatDate(dateStr: string): string {
        const date = new Date(dateStr);
        return (
            date.toLocaleDateString() +
            ' ' +
            date.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
        );
    }

    public async fetchData(focusDate: Date, focusRange: DateRange) {
        const values = [];
        for (let i = 0; i < 10; i++) {
            values.push({date: new Date(2020 + i, 0), value: 10});
        }
        const fakeData = [
            {
                label: '% Rainy',
                style: 'bar',
                values,
            },
            {
                label: '% Images',
                style: 'bar',
                values,
            },
            //  {
            //      label: '% Quality',
            //      style: 'line',
            //      values,
            //  },
        ];

        const range = mapDateRangeToString(focusRange);
        let data = fakeData;
        if (!this.rainNode) {
            return data;
        }

        if (focusRange === DateRange.CENTURY) {
            // fake
        } else if (focusRange === DateRange.HOUR) {
            const hourCounts = await this.profileService.getCountsHour(this.rainNode.id, {
                periodBegin: focusDate,
            });

            data = [
                {
                    label: 'Rainy Count',
                    style: 'line',
                    values: hourCounts.rainyCount.map(RaainDetailsComponent.MapCountToDateValue),
                },
                {
                    label: '% Images',
                    style: 'bar',
                    values: hourCounts.percentImages.map(RaainDetailsComponent.MapCountToDateValue),
                },
                {
                    label: 'Rainy Sum',
                    style: 'line',
                    values: hourCounts.rainySum.map(RaainDetailsComponent.MapCountToDateValue),
                },
            ];
        } else {
            const counts = await this.profileService.getCounts(this.rainNode.id, {
                range,
                periodBegin: focusDate,
            });

            data = [
                {
                    label: '% Rainy',
                    style: 'bar',
                    values: counts.percentRainy.map(RaainDetailsComponent.MapCountToDateValue),
                },
                {
                    label: '% Images',
                    style: 'bar',
                    values: counts.percentImages.map(RaainDetailsComponent.MapCountToDateValue),
                },
            ];
        }

        // console.log(`fetchData DONE ${range}`, data);
        return data;
    }

    // Wrapper function that preserves the async nature of fetchData
    public fetchDataWrapper = async (focusDate: Date, focusRange: DateRange) => {
        return await this.fetchData(focusDate, focusRange);
    };

    public ngOnChanges(changes: SimpleChanges): void {
        this.change(changes).then((ignored) => {});
    }

    ngOnDestroy() {
        this.cleanAll();
    }

    async onEnableCountHistoryTab(rain: RainNode) {
        if (!this.toggleHistory) {
            this.countPoints = [];
        }
    }

    async onPeriodBeginChange(event: any) {
        const newValue = event?.target?.value ?? this.periodBeginAsString;
        this.periodBegin = new Date(newValue);
        this.periodBeginAsString = RaainDetailsComponent.PeriodDisplay(this.periodBegin);
        this.storage.set('raain-periodBegin', this.periodBegin);

        await this.onPeriodDurationChange(event);
    }

    async onPeriodEndChange(event: any) {
        const newValue = event?.target?.value ?? this.periodEndAsString;
        this.periodEnd = new Date(newValue);
        this.periodEndAsString = RaainDetailsComponent.PeriodDisplay(this.periodEnd);
        this.storage.set('raain-periodEnd', this.periodEnd);

        this.periodBegin = new Date(
            this.periodEnd.getTime() - this.getDurationInHours() * RaainDetailsComponent.HOUR_MS
        );
        this.periodBeginAsString = RaainDetailsComponent.PeriodDisplay(this.periodBegin);
        this.storage.set('raain-periodBegin', this.periodBegin);
        this.updateRefreshManagerPeriod();
    }

    async onPeriodDurationChange(_event: object) {
        const durationInHours = this.getDurationInHours();
        this.periodEnd = new Date(
            this.periodBegin.getTime() + durationInHours * RaainDetailsComponent.HOUR_MS
        );
        this.periodEndAsString = RaainDetailsComponent.PeriodDisplay(this.periodEnd);
        this.storage.set('raain-periodEnd', this.periodEnd);
        this.storage.set('raain-periodDurationInHours', durationInHours);
        this.updateRefreshManagerPeriod();
        this.updateCumulativeDurationInMinutes();
    }

    async onDateChangeInCount(dateChanged: Date) {
        const dateString =
            dateChanged.toISOString().substring(0, 11) +
            dateChanged.toLocaleTimeString().substring(0, 5);
        this.periodDurationAsString = '1';

        if (this.toggleCumulative) {
            // Cumulative: select periodEnd
            this.periodEndAsString = dateString;
            await this.onPeriodEndChange(null);
        } else {
            // Granular: select periodBegin
            this.periodBeginAsString = dateString;
            await this.onPeriodBeginChange(null);
        }
        await this.refreshManager.refresh(false, this.toggleAdmin);
    }

    async onDateChangeInMap(dateShown: Date) {
        this.dateShown = dateShown;
        await this.fetchAndUpdateMap();
        await this.refreshManager.setReportPeriod(this.dateShown);
    }

    async onSumChangeInMap(sum: string) {
        this.sumDetails = sum;
    }

    async onGaugeSelectInMap(mapLatLng: MapLatLng) {
        const gaugesFiltered = this.compareManager.gaugesInMap.filter(
            (g) => g.lat === mapLatLng.lat && g.lng === mapLatLng.lng
        );
        if (gaugesFiltered.length !== 1) {
            return;
        }
        const gaugeSelected = gaugesFiltered[0];

        await this.refreshGaugeValues({id: gaugeSelected.id, name: gaugeSelected.name});

        await this.compareManager.selectGauge(gaugeSelected.id, 0);
    }

    async refreshGaugeValues(gaugeSelected: {id: string; name: string}) {
        const gaugeValueShowBegin = new Date(
            this.periodBegin.getTime() - RaainDetailsComponent.DAY_MS
        );
        const gaugeValueShowEnd = new Date(this.periodEnd.getTime() + RaainDetailsComponent.DAY_MS);
        gaugeValueShowBegin.setHours(0, 0);
        gaugeValueShowEnd.setHours(23, 59);

        const gaugeMeasures = await this.profileService.getGaugeMeasures(
            gaugeSelected.id,
            gaugeValueShowBegin,
            gaugeValueShowEnd
        );
        const gaugeValues: {date: Date; value: number}[] = gaugeMeasures.map((gm) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cartesianMeasureValue = new CartesianMeasureValue(gm.values[0] as any);
            return {
                date: gm.date,
                value: cartesianMeasureValue.getCartesianValues()[0].value,
            };
        });
        this.gaugeSelectedPoints = [
            {
                label: gaugeSelected.name,
                style: 'bar',
                values: gaugeValues,
            },
        ];
        this.cdr.markForCheck();
    }

    async onGaugeSelectInCompare(e: {point: XYType; compareIndex: number}) {
        await this.refreshGaugeValues({id: e.point.id, name: e.point.name});

        await this.compareManager.selectGauge(e.point.id, e.compareIndex);
    }

    async onToggleMap($event: Event) {
        // if (this.toggleMap) {
        //    await this.refreshMap();
        // }
    }

    onTogglePixelMarkers() {
        // Toggle is bound to showPixelMarkers, raain-map handles marker display
    }

    async toggleCumulativeChanged(_$event: Event) {
        this.storage.set('raain-toggleCumulative', this.toggleCumulative);
        this.dateShown = this.getDateBasedOnCumulativeMode(this.timeframeDates);
        this.updateCumulativeDurationInMinutes();
        if (this.toggleMap) {
            this.updateRefreshManagerPeriod(); // Update cumulative flag before refresh
            await this.refreshManager.refresh(false, this.toggleAdmin);
        }
    }

    updateCumulativeDurationInMinutes() {
        if (this.toggleCumulative) {
            // Cumulative mode: use period duration
            this.cumulativeDurationInMinutes = parseFloat(this.periodDurationAsString) * 60;
        } else {
            // Granular mode: use selectedTimeStep
            this.cumulativeDurationInMinutes = this.selectedTimeStep || 10;
        }
    }

    async onProviderChanged($event: Event) {
        this.selectedProvider = ($event as CustomEvent)?.detail?.value;
        this.storage.set('raain-selectedProvider', this.selectedProvider);
        await this.applyRefreshManagerSettings();
    }

    async onTimeStepChanged($event: Event) {
        this.selectedTimeStep = ($event as CustomEvent)?.detail?.value;
        this.storage.set('raain-selectedTimeStep', this.selectedTimeStep);
        this.updateCumulativeDurationInMinutes();
        await this.applyRefreshManagerSettings();
    }

    async loadProviders() {
        if (!this.rainNode?.id) {
            return;
        }

        try {
            const result = await this.profileService.getProviders(this.rainNode.id);
            this.availableProviders = result.providers;
            this.availableTimeSteps = result.timeStepInMinutes;

            // Load saved selections or use defaults
            this.selectedProvider =
                this.storage.get('raain-selectedProvider') ||
                (this.availableProviders.length > 0 ? this.availableProviders[0] : 'Raain');
            this.selectedTimeStep =
                this.storage.get('raain-selectedTimeStep') ||
                (this.availableTimeSteps.length > 0 ? this.availableTimeSteps[0] : 10);
        } catch (e) {
            // Set fallback values
            this.availableProviders = ['Raain'];
            this.availableTimeSteps = [5, 10, 15, 30, 60];
            this.selectedProvider = 'Raain';
            this.selectedTimeStep = 10;
        }

        // Set provider and timeStep on refreshManager
        if (this.refreshManager) {
            this.refreshManager.provider = this.selectedProvider;
            this.refreshManager.timeStepInMinutes = this.selectedTimeStep;
        }
        this.cdr.markForCheck();
    }

    onChangeDetectionTest(rainNode: RainNode) {
        console.log(TEST_DETECTION++, 'onChangeDetectionTest');
        return '';
    }

    updateTruncatedError() {
        const error = this.refreshManager?.lastError || '';
        if (error.length <= 80) {
            this.truncatedError = error;
            return;
        }
        this.truncatedError = error.substring(0, 80) + '...';
    }

    updateCachedValues() {
        this.updatePercentOfImages();
        this.updatePercentOfComputations();
        this.updateTruncatedError();
        this.updateCumulativeDurationInMinutes();
    }

    async refreshMap() {
        this.gaugeSelectedPoints = [];
        this.dateShown = this.getDateBasedOnCumulativeMode();
        this.borders = [];

        this.compareManager.cleanAll();
        await this.compareManager.setGaugesInMap();
        await this.refreshManager.refresh(false, this.toggleAdmin);
        this.cdr.markForCheck();
    }

    async setPeriodOfNow() {
        const last30mn = new Date();
        last30mn.setMinutes(last30mn.getMinutes() - 30);
        this.periodBeginAsString =
            last30mn.toISOString().substring(0, 11) + last30mn.toLocaleTimeString().substring(0, 5);
        this.periodDurationAsString = '1';
        await this.onPeriodBeginChange(null);
        await this.refreshManager.refresh(false, this.toggleAdmin);
    }

    updatePercentOfImages() {
        if (!this.countsPeriod?.percentImages?.length) {
            this.percentOfImages = 0;
            return;
        }

        const duringPeriod = this.countsPeriod.percentImages.filter(
            (a) =>
                this.periodBegin.getTime() <= new Date(a.name).getTime() &&
                new Date(a.name).getTime() <= this.periodEnd.getTime()
        );
        const summed = duringPeriod.reduce((a, b) => a + (b.x ?? 0), 0);
        this.percentOfImages = Math.round(summed / duringPeriod.length);
    }

    updatePercentOfComputations() {
        const timeline = this.refreshManager?.getTimelineFrameSet();
        if (!timeline?.length) {
            this.percentOfComputations = 0;
            return;
        }

        const timelineWithComputation = timeline.filter(
            (a) => !!a.rainComputationCumulativeId || !!a.rainComputationId
        );
        const ratio = timelineWithComputation.length / timeline.length;
        this.percentOfComputations = Math.round(ratio * 100);
    }

    private getDateBasedOnCumulativeMode(fallbackDates?: Date[]): Date {
        if (fallbackDates?.length > 0) {
            const dateExists = fallbackDates.some((d) => d.getTime() === this.dateShown?.getTime());
            if (!dateExists) {
                return this.toggleCumulative
                    ? fallbackDates[fallbackDates.length - 1]
                    : fallbackDates[0];
            }
        }
        return this.toggleCumulative ? this.periodEnd : this.periodBegin;
    }

    private getCumulativeHours(): number {
        return this.toggleCumulative ? parseFloat(this.periodDurationAsString) : 0;
    }

    private getDurationInHours(): number {
        return parseFloat(this.periodDurationAsString);
    }

    private updateRefreshManagerPeriod() {
        this.refreshManager.cumulative = this.toggleCumulative;

        // Align dates to 5-minute boundaries (floor) for consistency with raain-ground
        const alignTo5minFloor = (date: Date): Date => {
            const minutes = date.getMinutes();
            const alignedMinutes = Math.floor(minutes / 5) * 5;
            return new Date(
                date.getFullYear(),
                date.getMonth(),
                date.getDate(),
                date.getHours(),
                alignedMinutes,
                0,
                0
            );
        };

        this.refreshManager.period = {
            begin: alignTo5minFloor(this.periodBegin),
            end: alignTo5minFloor(this.periodEnd),
        };
    }

    private async fetchAndUpdateMap() {
        await this.refreshManager.fetch(
            this.dateShown,
            this.toggleGaugeMeasures,
            this.getCumulativeHours()
        );
        this.currentTimeframeTarget = this.refreshManager.getTimelineSelectedFrameSet();
        this.cdr.markForCheck();
    }

    private async applyRefreshManagerSettings() {
        if (!this.refreshManager) {
            return;
        }
        this.refreshManager.provider = this.selectedProvider;
        this.refreshManager.timeStepInMinutes = this.selectedTimeStep;
        await this.refreshManager.refresh(false, this.toggleAdmin);
    }

    private cleanAll() {
        this.borders = [];
        this.isAdmin = false;
        this.timeframeContainers = new TimeframeContainers([]);
        this.currentTimeframeTarget = null;
        this.timeframeDates = [];
        this.countPoints = [];
        this.countsPeriod = {progress: 0, queueRunning: 0, percentImages: []};
        this.gaugeSelectedPoints = [];

        this.toggleHistory = false;
        this.toggleMap = true;
        this.toggleCompare = false;
        this.toggleGaugeMeasures = false;
        this.toggleRemoveCompareDuplicate = true;
        this.toggleCumulative = this.storage.get('raain-toggleCumulative');

        this.periodBegin = new Date(this.storage.get('raain-periodBegin'));
        this.periodEnd = new Date(this.storage.get('raain-periodEnd'));
        this.periodBeginAsString = RaainDetailsComponent.PeriodDisplay(this.periodBegin);
        this.periodEndAsString = RaainDetailsComponent.PeriodDisplay(this.periodEnd);
        const durationMs = this.periodEnd.getTime() - this.periodBegin.getTime();
        this.periodDurationAsString = '' + durationMs / RaainDetailsComponent.HOUR_MS;

        this.dateShown = this.getDateBasedOnCumulativeMode();
        this.refreshInProgress = false;
        this.showFullError = false;
        this.showQualityModal = false;
        this.qualityIndicators = [];
        this.qualityIndicatorsLoading = false;

        this.compareManager?.cleanAll();
        this.refreshManager?.cleanAll();
    }

    private async init() {
        this.cleanAll();
        this.updateCachedValues();
        await this.initRain();
        this.cdr.markForCheck();
    }

    private async initRain() {
        if (!this.rainNode) {
            return;
        }

        this.isAdmin = this.profileService.isAdmin();

        this.refreshManager.rainNode = this.rainNode;
        this.compareManager.rainNode = this.rainNode;

        // Load providers and set on refreshManager
        await this.loadProviders();

        this.refreshManager.setMethods(
            this.onRefreshInProgress.bind(this),
            this.onRefreshDone.bind(this),
            this.onFetchDone.bind(this)
        );

        const center = this.rainNode.getCenter();
        this.coordinates = new MapLatLng(center.lat, center.lng);
        this.teamNode = await this.profileService.getTeam(
            this.rainNode.getLink(TeamNode.TYPE).getId()
        );

        // Load all gauges linked to the rainNode on map
        await this.compareManager.setGaugesInMap();

        if (this.periodBegin && this.periodEnd) {
            this.updateRefreshManagerPeriod();
            await this.refreshManager.refresh(false, this.toggleAdmin);
        }
    }

    private async onRefreshInProgress(
        countPeriods: {
            percentImages?: XYType[];
            queueRunning?: number;
            progress?: number;
        },
        timeframeDates: Date[]
    ) {
        this.refreshInProgress = true;
        this.countsPeriod = countPeriods;
        this.timeframeDates = timeframeDates;
        this.updateCachedValues();
        this.cdr.markForCheck();
    }

    private async onRefreshDone(timeframeDates: Date[]) {
        this.timeframeDates = timeframeDates;
        this.refreshInProgress = false;
        this.updateCachedValues();
        this.cdr.markForCheck();

        if (this.toggleMap && timeframeDates.length > 0) {
            this.dateShown = this.getDateBasedOnCumulativeMode(timeframeDates);
            if (this.dateShown) {
                await this.fetchAndUpdateMap();
            }
        }
    }

    private async onFetchDone(timeframeContainers?: TimeframeContainers) {
        if (timeframeContainers) {
            this.timeframeContainers = timeframeContainers;
        }
        this.cdr.markForCheck();
    }

    private async change(_changes: SimpleChanges) {
        await this.init();
    }
}
